import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { AuthProvider } from 'react-oidc-context'
import { router } from './routes'
import { config, authEnabled } from './config'
// Both stylesheets are global: the dashboard pages reuse the editor's
// transport, nudge-group and canvas rules, and the pages load lazily
// (routes.tsx), so the editor's sheet cannot ride in with its chunk.
// Editor sheet first, dashboard second, as the cascade has always been.
import '@/styles/edl-editor.css'
import '@/styles/dashboard.css'
import React from 'react'

// Cognito managed login via OIDC code + PKCE. The bearer sent to the API is
// the ID token (see api/client.ts). onSigninCallback only strips ?code&state
// from the URL; CallbackPage handles navigating back to the stored path.
//
// The pool serves its login pages from two hosts: the branded login domain
// (id.reframer.pro) and the generated amazoncognito.com prefix domain.
// Discovery on the issuer picks one of them for us, so when a domain is
// configured the endpoints are pinned to it explicitly: the SPA always
// sends users to VITE_COGNITO_DOMAIN, whichever host discovery would name.
const { authority, domain } = config.cognito;
const metadata = domain
  ? {
      issuer: authority!,
      jwks_uri: `${authority}/.well-known/jwks.json`,
      authorization_endpoint: `https://${domain}/oauth2/authorize`,
      token_endpoint: `https://${domain}/oauth2/token`,
      userinfo_endpoint: `https://${domain}/oauth2/userInfo`,
      revocation_endpoint: `https://${domain}/oauth2/revoke`,
      end_session_endpoint: `https://${domain}/logout`,
    }
  : undefined;

const oidcConfig = {
  authority: authority!,
  client_id: config.cognito.clientId!,
  metadata,
  redirect_uri: `${window.location.origin}/callback`,
  response_type: 'code',
  scope: 'openid email profile',
  automaticSilentRenew: true,
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, '/callback')
  },
}

const app = <RouterProvider router={router} />

createRoot(document.getElementById('root')!).render(
  authEnabled ? <AuthProvider {...oidcConfig}>{app}</AuthProvider> : app
)
